<script setup>
import {
  ref,
  computed,
  onMounted,
  onBeforeUnmount,
  watch,
  nextTick,
} from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { useRouter, useRoute } from 'vue-router';

import Button from 'dashboard/components-next/button/Button.vue';
import Avatar from 'next/avatar/Avatar.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import WootMessageEditor from 'dashboard/components/widgets/WootWriter/Editor.vue';
import ResolveTicketModal from './ResolveTicketModal.vue';
import GenerateOrderModal from './GenerateOrderModal.vue';
import TicketCategoriesAPI from 'dashboard/api/ticketCategories';
import TicketAttachments from 'dashboard/components/tickets/TicketAttachments.vue';
import DocumentRequestsPanel from 'dashboard/components/documents/DocumentRequestsPanel.vue';
import DocumentFileList from 'dashboard/components/documents/DocumentFileList.vue';
import SignatureRequestsPanel from 'dashboard/components/signatures/SignatureRequestsPanel.vue';
import ticketDocumentRequestsApi from 'dashboard/api/ticketDocumentRequests';
import ticketDocumentsApi from 'dashboard/api/ticketDocuments';
import TicketsAPI from 'dashboard/api/tickets';
import TicketCustomAttributes from 'dashboard/components/tickets/TicketCustomAttributes.vue';
import TicketConversationThread from './components/TicketConversationThread.vue';
import ServiceOrdersAPI from 'dashboard/api/serviceOrders';
import TasksAPI from 'dashboard/api/tasks';
import TaskDetails from '../tasks/TaskDetails.vue';
import { useMessageFormatter } from 'shared/composables/useMessageFormatter';
import { introduccionLectura } from 'dashboard/api/cea';
import { emitter } from 'shared/helpers/mitt';
import { useAccount } from 'dashboard/composables/useAccount';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';

const props = defineProps({
  ticketId: {
    type: [String, Number],
    default: 0,
  },
  embedded: {
    type: Boolean,
    default: false,
  },
});

// When embedded in the list modal, navigation/close are handled by the parent
// (emit) instead of routing to a new page.
const emit = defineEmits(['close', 'navigate']);

const { t } = useI18n();
const store = useStore();
const router = useRouter();
const route = useRoute();
const { formatMessage } = useMessageFormatter();

const resolvedTicketId = computed(
  () => props.ticketId || route.params.ticketId
);

// Issues the public document-request share token for the current ticket.
const documentShareFn = id => TicketsAPI.generateDocumentRequestShare(id);

// CEA feature flags — gate contract & service-order UI per account
const { isCloudFeatureEnabled } = useAccount();
const isContractsEnabled = computed(() =>
  isCloudFeatureEnabled(FEATURE_FLAGS.CONTRACTS)
);
const isServiceOrdersEnabled = computed(() =>
  isCloudFeatureEnabled(FEATURE_FLAGS.SERVICE_ORDERS)
);
const isTasksEnabled = computed(() =>
  isCloudFeatureEnabled(FEATURE_FLAGS.TASKS)
);

// State
const serviceOrders = ref([]);
const linkedTasks = ref([]);
const isCreatingTask = ref(false);
const newTaskTitle = ref('');
const newComment = ref('');
const isPrivate = ref(false);
// When a note is private, the agent may opt to surface it to the client in the App Studio
// portal ("Visible al cliente"). Only meaningful while isPrivate is on.
const clientVisible = ref(false);
const showResolveModal = ref(false);
const showGenerateOrderModal = ref(false);
const showFollowUpModal = ref(false);
const isSendingLectura = ref(false);
const isLoading = ref(true);
let typingTimer = null;
let countdownTimer = null;

// Status dropdown
const statusOpen = ref(false);
const statusRef = ref(null);

// Priority dropdown
const priorityOpen = ref(false);
const priorityRef = ref(null);

// Assignee searchable dropdown
const assigneeOpen = ref(false);
const assigneeSearch = ref('');
const assigneeRef = ref(null);
const assigneeSearchInput = ref(null);

// Team searchable dropdown
const teamOpen = ref(false);
const teamSearch = ref('');
const teamRef = ref(null);
const teamSearchInput = ref(null);

// Overflow (⋯) lifecycle actions menu
const actionsOpen = ref(false);
const actionsRef = ref(null);

// Category / subcategory dropdowns (custom, matching the other fields)
const editCategoryId = ref(null);
const editSubcategoryId = ref(null);
const isSavingCategory = ref(false);
const categoryOpen = ref(false);
const categoryRef = ref(null);
const subcategoryOpen = ref(false);
const subcategoryRef = ref(null);

// Contract editing state
const isEditingContract = ref(false);
const editContract = ref('');
const isSavingContract = ref(false);

// Address editing state (single pencil toggles all 5 fields together)
const isEditingAddress = ref(false);
const editAddress = ref({
  address_street: '',
  address_exterior_number: '',
  address_interior_number: '',
  address_colony: '',
  address_postal_code: '',
  address_city: '',
  address_state: '',
});
const isSavingAddress = ref(false);

// Follow-up state
const followUpTitle = ref('');
const followUpDescription = ref('');
const isCreatingFollowUp = ref(false);

// Verification countdown
const verificationCountdown = ref('');

// Getters
const agents = useMapGetter('agents/getAgents');
const teams = useMapGetter('teams/getTeams');

// Computed
const ticket = computed(() => {
  return store.getters['tickets/getTicketById'](
    parseInt(resolvedTicketId.value, 10)
  );
});

const ticketConversations = computed(() => {
  const displayId = ticket.value?.display_id || resolvedTicketId.value;
  return store.getters['ticketConversations/getByTicketId'](displayId);
});

const typingUsers = computed(() => {
  const displayId = ticket.value?.display_id || resolvedTicketId.value;
  return store.getters['ticketTypingStatus/getUserList'](displayId);
});

const typingUserNames = computed(() => {
  if (!typingUsers.value.length) return '';
  const names = typingUsers.value.map(u => u.name);
  if (names.length === 1) return `${names[0]} ${t('TICKETS.TYPING.IS_TYPING')}`;
  return `${names.join(', ')} ${t('TICKETS.TYPING.ARE_TYPING')}`;
});

const isOverdue = computed(() => {
  if (!ticket.value?.sla_deadline) return false;
  return new Date(ticket.value.sla_deadline) < new Date();
});

const isVerificationPending = computed(() => {
  return ticket.value?.verification_status === 'pending';
});

const isVerificationExpired = computed(() => {
  if (!isVerificationPending.value) return false;
  if (!ticket.value?.verification_expires_at) return false;
  return new Date(ticket.value.verification_expires_at) <= new Date();
});

const canCloseTicket = computed(() => {
  if (ticket.value?.status !== 'resolved') return false;
  if (isVerificationPending.value && !isVerificationExpired.value) return false;
  return true;
});

const canGenerateOrder = computed(() => {
  const code = ticket.value?.ticket_category?.code;
  return code ? ['REP', 'SRV'].includes(code) : false;
});

const verificationStatusLabel = computed(() => {
  const status = ticket.value?.verification_status;
  if (!status || status === 'not_required') return null;
  const map = {
    pending: t('TICKETS.VERIFICATION.STATUS.PENDING'),
    verified: t('TICKETS.VERIFICATION.STATUS.VERIFIED'),
    rejected: t('TICKETS.VERIFICATION.STATUS.REJECTED'),
  };
  return map[status];
});

const statusOptions = computed(() => [
  { value: 'open', label: t('TICKETS.STATUS.OPEN') },
  { value: 'in_progress', label: t('TICKETS.STATUS.IN_PROGRESS') },
  { value: 'escalated', label: t('TICKETS.STATUS.ESCALATED') },
  { value: 'waiting_client', label: t('TICKETS.STATUS.WAITING_CLIENT') },
]);

const priorityOptions = computed(() => [
  { value: 'low', label: t('TICKETS.PRIORITY.LOW') },
  { value: 'medium', label: t('TICKETS.PRIORITY.MEDIUM') },
  { value: 'high', label: t('TICKETS.PRIORITY.HIGH') },
  { value: 'urgent', label: t('TICKETS.PRIORITY.URGENT') },
]);

const currentPriorityLabel = computed(
  () =>
    priorityOptions.value.find(o => o.value === ticket.value?.priority)
      ?.label || ticket.value?.priority
);

const filteredAgents = computed(() => {
  const q = assigneeSearch.value.toLowerCase();
  const all = [
    { id: null, name: t('TICKETS.FORM.NO_ASSIGNEE') },
    ...(agents.value || []),
  ];
  if (!q) return all;
  return all.filter(a => a.name.toLowerCase().includes(q));
});

const filteredTeams = computed(() => {
  const q = teamSearch.value.toLowerCase();
  const all = [
    { id: null, name: t('TICKETS.FORM.NO_TEAM') },
    ...(teams.value || []),
  ];
  if (!q) return all;
  return all.filter(team => team.name.toLowerCase().includes(q));
});

const currentAssigneeName = computed(() => {
  if (!ticket.value?.assignee_id) return t('TICKETS.FORM.NO_ASSIGNEE');
  return (
    agents.value?.find(a => a.id === ticket.value.assignee_id)?.name ||
    t('TICKETS.FORM.NO_ASSIGNEE')
  );
});

const currentTeamName = computed(() => {
  if (!ticket.value?.team_id) return t('TICKETS.FORM.NO_TEAM');
  return (
    teams.value?.find(team => team.id === ticket.value.team_id)?.name ||
    t('TICKETS.FORM.NO_TEAM')
  );
});

const ticketWorkflowFields = computed(() => {
  const subcategory = ticket.value?.ticket_subcategory;
  if (!subcategory?.workflow_config?.fields) return [];
  return subcategory.workflow_config.fields;
});

const verificationStatusClasses = computed(() => {
  const status = ticket.value?.verification_status;
  const map = {
    pending: 'bg-n-amber-9/20 text-n-amber-11',
    verified: 'bg-n-teal-9/20 text-n-teal-11',
    rejected: 'bg-n-ruby-9/20 text-n-ruby-11',
  };
  return map[status] || '';
});

// Methods
const goBack = () => {
  if (props.embedded) {
    emit('close');
    return;
  }
  router.push({ name: 'tickets_list' });
};

const fetchTicket = async () => {
  isLoading.value = true;
  try {
    const response = await store.dispatch('tickets/fetchTicket', {
      id: resolvedTicketId.value,
    });
    return response;
  } finally {
    isLoading.value = false;
  }
};

const fetchConversations = async (ticketData = null) => {
  const ticketId =
    ticketData?.display_id ||
    ticket.value?.display_id ||
    resolvedTicketId.value;
  await store.dispatch('ticketConversations/fetch', ticketId);
};

const fetchServiceOrders = async (ticketData = null) => {
  try {
    // Wait a bit for ticket.value to be populated
    await new Promise(resolve => {
      setTimeout(() => resolve(), 100);
    });

    const ticketId = ticket.value?.id || ticketData?.id;
    if (!ticketId) return;

    const response = await ServiceOrdersAPI.get({ ticket_id: ticketId });
    serviceOrders.value = response.data.payload || [];
  } catch (error) {
    serviceOrders.value = [];
  }
};

const fetchLinkedTasks = async (ticketData = null) => {
  if (!isTasksEnabled.value) return;
  const ticketId = ticket.value?.id || ticketData?.id;
  if (!ticketId) return;
  try {
    const response = await TasksAPI.get({
      taskable_type: 'Ticket',
      taskable_id: ticketId,
    });
    linkedTasks.value = response.data.payload || [];
  } catch (e) {
    linkedTasks.value = [];
  }
};

const createTaskForTicket = async () => {
  if (!newTaskTitle.value.trim()) return;
  const ticketId = ticket.value?.id;
  if (!ticketId) return;
  isCreatingTask.value = true;
  try {
    await TasksAPI.create({
      title: newTaskTitle.value,
      taskable_type: 'Ticket',
      taskable_id: ticketId,
    });
    newTaskTitle.value = '';
    await fetchLinkedTasks();
  } finally {
    isCreatingTask.value = false;
  }
};

// Linked tasks open in a modal over the ticket (no page navigation), so
// closing returns the user to the ticket exactly where they were.
const selectedTaskId = ref(null);
const isTaskModalOpen = computed(() => Boolean(selectedTaskId.value));

const goToTask = displayId => {
  selectedTaskId.value = displayId;
};
const closeTaskModal = () => {
  selectedTaskId.value = null;
  fetchLinkedTasks(); // reconcile any edits made in the modal
};
const onTaskModalNavigate = displayId => {
  selectedTaskId.value = displayId;
};

const sendTypingOn = () => {
  store.dispatch('ticketTypingStatus/toggleTyping', {
    ticketId: ticket.value?.display_id,
    status: 'on',
    isPrivate: isPrivate.value,
  });
};

const sendTypingOff = () => {
  store.dispatch('ticketTypingStatus/toggleTyping', {
    ticketId: ticket.value?.display_id,
    status: 'off',
    isPrivate: isPrivate.value,
  });
};

const addComment = async () => {
  if (!newComment.value.trim()) return;

  try {
    sendTypingOff();
    await store.dispatch('ticketConversations/create', {
      ticketId: ticket.value.display_id,
      message: newComment.value,
      isPrivate: isPrivate.value,
      // Only a private note can be selectively shared with the client.
      clientVisible: isPrivate.value && clientVisible.value,
    });
    newComment.value = '';
    isPrivate.value = false;
    clientVisible.value = false;
  } catch (error) {
    useAlert(t('TICKETS.COMMENT_ERROR'));
  }
};

const onCommentInput = () => {
  if (typingTimer) clearTimeout(typingTimer);
  sendTypingOn();
  typingTimer = setTimeout(() => {
    sendTypingOff();
  }, 4000);
};

const updateStatus = async newStatus => {
  // Check if trying to resolve/close ticket with pending service orders
  if (newStatus === 'resolved' || newStatus === 'closed') {
    const hasPendingOrders =
      serviceOrders.value?.some(
        order =>
          !['field_complete', 'redirected', 'cancelled', 'merged'].includes(
            order.status
          )
      ) || false;

    if (hasPendingOrders) {
      useAlert(t('TICKETS.SIDEBAR.PENDING_ORDERS_ERROR'));
      return;
    }
  }

  await store.dispatch('tickets/updateTicket', {
    id: ticket.value.display_id,
    status: newStatus,
  });
  await fetchTicket();
  emitter.emit('ticket_updated');
};

const updatePriority = async newPriority => {
  await store.dispatch('tickets/updateTicket', {
    id: ticket.value.display_id,
    priority: newPriority,
  });
  await fetchTicket();
  emitter.emit('ticket_updated');
};

const updateAssignee = async assigneeId => {
  if (assigneeId) {
    await store.dispatch('tickets/assignTicket', {
      id: ticket.value.display_id,
      assigneeId,
    });
  } else {
    // Unassign the ticket
    await store.dispatch('tickets/updateTicket', {
      id: ticket.value.display_id,
      assignee_id: null,
    });
  }
  await fetchTicket();
  emitter.emit('ticket_updated');
};

const updateTeam = async teamId => {
  await store.dispatch('tickets/updateTicket', {
    id: ticket.value.display_id,
    team_id: teamId ? parseInt(teamId, 10) : null,
  });
  await fetchTicket();
  emitter.emit('ticket_updated');
};

const closeTicket = async () => {
  // Check for pending service orders before closing
  const hasPendingOrders =
    serviceOrders.value?.some(
      order =>
        !['field_complete', 'redirected', 'cancelled', 'merged'].includes(
          order.status
        )
    ) || false;

  if (hasPendingOrders) {
    useAlert(t('TICKETS.SIDEBAR.PENDING_ORDERS_ERROR'));
    return;
  }

  if (isVerificationPending.value && !isVerificationExpired.value) {
    useAlert(t('TICKETS.VERIFICATION.CANNOT_CLOSE'));
    return;
  }

  try {
    await store.dispatch('tickets/closeTicket', ticket.value.display_id);
    await fetchTicket();
    emitter.emit('ticket_updated');
    useAlert(t('TICKETS.CLOSED_SUCCESS'));
  } catch (error) {
    useAlert(
      error?.response?.data?.error || t('TICKETS.VERIFICATION.CANNOT_CLOSE')
    );
  }
};

const reopenTicket = async () => {
  if (ticket.value.status === 'closed') {
    useAlert(t('TICKETS.CANNOT_REOPEN_CLOSED'));
    return;
  }
  try {
    await store.dispatch('tickets/reopenTicket', ticket.value.display_id);
    await fetchTicket();
    emitter.emit('ticket_updated');
    useAlert(t('TICKETS.REOPENED_SUCCESS'));
  } catch (error) {
    useAlert(error?.response?.data?.error || t('TICKETS.CANNOT_REOPEN_CLOSED'));
  }
};

const verifyTicket = async () => {
  try {
    await store.dispatch('tickets/verifyTicket', {
      id: ticket.value.display_id,
    });
    await fetchTicket();
    emitter.emit('ticket_updated');
    useAlert(t('TICKETS.VERIFICATION.VERIFIED_SUCCESS'));
  } catch (error) {
    useAlert(t('TICKETS.VERIFICATION.VERIFY_ERROR'));
  }
};

const rejectVerification = async () => {
  try {
    await store.dispatch('tickets/rejectVerification', {
      id: ticket.value.display_id,
    });
    await fetchTicket();
    emitter.emit('ticket_updated');
    useAlert(t('TICKETS.VERIFICATION.REJECTED_SUCCESS'));
  } catch (error) {
    useAlert(t('TICKETS.VERIFICATION.REJECT_ERROR'));
  }
};

const openFollowUpModal = () => {
  followUpTitle.value = `${t('TICKETS.FOLLOW_UP.TITLE_PREFIX')}${ticket.value.title}`;
  followUpDescription.value = '';
  showFollowUpModal.value = true;
};

const createFollowUp = async () => {
  if (!followUpTitle.value.trim()) return;

  isCreatingFollowUp.value = true;
  try {
    const response = await store.dispatch('tickets/createFollowUpTicket', {
      id: ticket.value.display_id,
      title: followUpTitle.value,
      description: followUpDescription.value,
    });
    showFollowUpModal.value = false;
    useAlert(t('TICKETS.FOLLOW_UP.CREATED_SUCCESS'));
    // Navigate to the new follow-up ticket — within the modal when embedded.
    if (response?.display_id) {
      if (props.embedded) {
        emit('navigate', response.display_id);
      } else {
        router.push({
          name: 'ticket_details',
          params: { ticketId: response.display_id },
        });
      }
    }
  } catch (error) {
    useAlert(t('TICKETS.FOLLOW_UP.CREATE_ERROR'));
  } finally {
    isCreatingFollowUp.value = false;
  }
};

const openResolveModal = () => {
  // Check for pending service orders before opening resolve modal
  const hasPendingOrders =
    serviceOrders.value?.some(
      order =>
        !['field_complete', 'redirected', 'cancelled', 'merged'].includes(
          order.status
        )
    ) || false;

  if (hasPendingOrders) {
    useAlert(t('TICKETS.SIDEBAR.PENDING_ORDERS_ERROR'));
    return;
  }

  showResolveModal.value = true;
};

const onResolved = () => {
  showResolveModal.value = false;
  fetchTicket();
};

const goToOrder = orderId => {
  router.push({ name: 'order_details', params: { orderId } });
};

// Search for a reading value (number + m³) in description or conversations
const lecturaValue = computed(() => {
  const LECTURA_REGEX = /(\d+(?:[.,]\d+)?)\s*m[³3]/i;

  const descMatch = (ticket.value?.description || '').match(LECTURA_REGEX);
  if (descMatch) return descMatch[1];

  const convs = ticketConversations.value || [];
  for (let i = convs.length - 1; i >= 0; i -= 1) {
    const match = (convs[i]?.message || '').match(LECTURA_REGEX);
    if (match) return match[1];
  }
  return null;
});

const canSendLectura = computed(
  () =>
    ticket.value?.ticket_subcategory?.code === 'FAC-LEC' &&
    !!ticket.value?.contract_number &&
    !!lecturaValue.value
);

const sendLectura = async () => {
  if (!canSendLectura.value || isSendingLectura.value) return;
  if (!lecturaValue.value) {
    useAlert('No se encontró ninguna lectura (m³) en la actividad del ticket');
    return;
  }
  try {
    isSendingLectura.value = true;
    const xmlDoc = await introduccionLectura({
      lecturaContador1: lecturaValue.value,
      numeroContrato: ticket.value.contract_number,
    });
    const cod = parseInt(
      xmlDoc.getElementsByTagName('codigoError')[0]?.textContent || '0',
      10
    );
    const desc =
      xmlDoc.getElementsByTagName('descripcionError')[0]?.textContent || '';
    if (cod === 0) {
      useAlert('Lectura enviada exitosamente a Aquasis');
    } else {
      useAlert(`Error al enviar lectura: ${desc} (código ${cod})`);
    }
  } catch {
    useAlert('Error al conectar con Aquasis');
  } finally {
    isSendingLectura.value = false;
  }
};

const generateOrder = () => {
  showGenerateOrderModal.value = true;
};

const onOrderGenerated = orderData => {
  showGenerateOrderModal.value = false;
  // Optionally open the generated PDF
  if (orderData.pdf_url) {
    window.open(orderData.pdf_url, '_blank');
  }
};

// Category / subcategory — separate fields (CategorySelector), auto-saved.
// Keep the edit refs in sync with the ticket so the selectors show current values.
watch(
  () => [ticket.value?.ticket_category_id, ticket.value?.ticket_subcategory_id],
  ([cat, sub]) => {
    editCategoryId.value = cat || null;
    editSubcategoryId.value = sub || null;
  },
  { immediate: true }
);

const saveCategory = async () => {
  if (!editCategoryId.value || !editSubcategoryId.value) return;
  isSavingCategory.value = true;
  try {
    await store.dispatch('tickets/updateTicket', {
      id: ticket.value.display_id,
      ticket_category_id: editCategoryId.value,
      ticket_subcategory_id: editSubcategoryId.value,
    });
    await fetchTicket();
    emitter.emit('ticket_updated');
    useAlert(t('TICKETS.EDIT_CATEGORY.SUCCESS'));
  } catch (error) {
    useAlert(t('TICKETS.EDIT_CATEGORY.ERROR'));
  } finally {
    isSavingCategory.value = false;
  }
};

// Tipo / Subtipo rendered as two metadata rows (custom dropdowns), matching the
// other fields. Categories (with nested subcategories) come from the API.
const ticketCategories = ref([]);
const fetchTicketCategories = async () => {
  try {
    const { data } = await TicketCategoriesAPI.get({ active_only: 'true' });
    ticketCategories.value = data || [];
  } catch {
    ticketCategories.value = [];
  }
};

const currentSubcategories = computed(() => {
  const cat = ticketCategories.value.find(
    c => c.id === Number(editCategoryId.value)
  );
  return cat?.ticket_subcategories || [];
});

const categoryMenuOptions = computed(() =>
  ticketCategories.value.map(c => ({ value: c.id, label: c.name }))
);
const subcategoryMenuOptions = computed(() =>
  currentSubcategories.value.map(s => ({ value: s.id, label: s.name }))
);
const categoryMenuLabel = computed(
  () =>
    ticketCategories.value.find(c => c.id === Number(editCategoryId.value))
      ?.name ||
    ticket.value?.ticket_category?.name ||
    t('TICKET_CATEGORIES.SELECTOR.SELECT_CATEGORY')
);
const subcategoryMenuLabel = computed(() => {
  const fromList = currentSubcategories.value.find(
    s => s.id === Number(editSubcategoryId.value)
  )?.name;
  if (fromList) return fromList;
  // Only show the ticket's saved subtype while the category is unchanged; once
  // the category is switched, the subtype is reset to avoid a mismatch.
  if (
    Number(editCategoryId.value) === Number(ticket.value?.ticket_category_id)
  ) {
    return ticket.value?.ticket_subcategory?.name || '-';
  }
  return '-';
});

const onSelectCategory = id => {
  categoryOpen.value = false;
  if (Number(id) === Number(editCategoryId.value)) return;
  editCategoryId.value = id;
  editSubcategoryId.value = null; // wait for a fresh subcategory before saving
};
const onSelectSubcategory = id => {
  subcategoryOpen.value = false;
  editSubcategoryId.value = id;
  saveCategory();
};

// Contract editing methods
const startEditingContract = () => {
  editContract.value = ticket.value?.contract_number || '';
  isEditingContract.value = true;
};

const cancelEditingContract = () => {
  isEditingContract.value = false;
  editContract.value = '';
};

const saveContract = async () => {
  isSavingContract.value = true;
  try {
    await store.dispatch('tickets/updateTicket', {
      id: ticket.value.display_id,
      contract_number: editContract.value.trim(),
    });
    await fetchTicket();
    emitter.emit('ticket_updated');
    isEditingContract.value = false;
    useAlert(t('TICKETS.EDIT_CONTRACT.SUCCESS'));
  } catch (error) {
    useAlert(t('TICKETS.EDIT_CONTRACT.ERROR'));
  } finally {
    isSavingContract.value = false;
  }
};

// Address editing methods
const startEditingAddress = () => {
  const tk = ticket.value || {};
  editAddress.value = {
    address_street: tk.address_street || '',
    address_exterior_number: tk.address_exterior_number || '',
    address_interior_number: tk.address_interior_number || '',
    address_colony: tk.address_colony || '',
    address_postal_code: tk.address_postal_code || '',
    address_city: tk.address_city || '',
    address_state: tk.address_state || '',
  };
  isEditingAddress.value = true;
};

const cancelEditingAddress = () => {
  isEditingAddress.value = false;
};

const saveAddress = async () => {
  isSavingAddress.value = true;
  try {
    await store.dispatch('tickets/updateTicket', {
      id: ticket.value.display_id,
      address_street: editAddress.value.address_street.trim(),
      address_exterior_number: editAddress.value.address_exterior_number.trim(),
      address_interior_number: editAddress.value.address_interior_number.trim(),
      address_colony: editAddress.value.address_colony.trim(),
      address_postal_code: editAddress.value.address_postal_code.trim(),
      address_city: editAddress.value.address_city.trim(),
      address_state: editAddress.value.address_state.trim(),
    });
    await fetchTicket();
    emitter.emit('ticket_updated');
    isEditingAddress.value = false;
    useAlert(t('TICKETS.EDIT_ADDRESS.SUCCESS'));
  } catch (error) {
    useAlert(t('TICKETS.EDIT_ADDRESS.ERROR'));
  } finally {
    isSavingAddress.value = false;
  }
};

const selectStatus = async value => {
  statusOpen.value = false;
  await updateStatus(value);
};

const selectPriority = async value => {
  priorityOpen.value = false;
  await updatePriority(value);
};

const selectAssignee = async agentId => {
  assigneeOpen.value = false;
  assigneeSearch.value = '';
  await updateAssignee(agentId);
};

const selectTeam = async teamId => {
  teamOpen.value = false;
  teamSearch.value = '';
  await updateTeam(teamId);
};

const closeDropdowns = e => {
  if (statusRef.value && !statusRef.value.contains(e.target))
    statusOpen.value = false;
  if (priorityRef.value && !priorityRef.value.contains(e.target))
    priorityOpen.value = false;
  if (assigneeRef.value && !assigneeRef.value.contains(e.target)) {
    assigneeOpen.value = false;
    assigneeSearch.value = '';
  }
  if (teamRef.value && !teamRef.value.contains(e.target)) {
    teamOpen.value = false;
    teamSearch.value = '';
  }
  if (actionsRef.value && !actionsRef.value.contains(e.target))
    actionsOpen.value = false;
  if (categoryRef.value && !categoryRef.value.contains(e.target))
    categoryOpen.value = false;
  if (subcategoryRef.value && !subcategoryRef.value.contains(e.target))
    subcategoryOpen.value = false;
};

const formatDate = date => {
  return new Date(date).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const getStatusLabel = status => {
  const statusMap = {
    open: t('TICKETS.STATUS.OPEN'),
    in_progress: t('TICKETS.STATUS.IN_PROGRESS'),
    escalated: t('TICKETS.STATUS.ESCALATED'),
    waiting_client: t('TICKETS.STATUS.WAITING_CLIENT'),
    waiting_internal: t('TICKETS.STATUS.WAITING_INTERNAL'),
    resolved: t('TICKETS.STATUS.RESOLVED'),
    closed: t('TICKETS.STATUS.CLOSED'),
    cancelled: t('TICKETS.STATUS.CANCELLED'),
  };
  return statusMap[status] || status;
};

// ── Tasks-style detail helpers ───────────────────────────────────────────────
// Right-panel tabs: actividad (comments + activity feed) · conversation
const activeTab = ref('activity');

// Solid status pills (ClickUp-style) on Radix `n-` scales → dark-mode safe.
const STATUS_PILL = {
  open: 'bg-n-slate-9 text-white',
  in_progress: 'bg-n-blue-9 text-white',
  escalated: 'bg-n-ruby-9 text-white',
  waiting_client: 'bg-n-amber-9 text-white',
  waiting_internal: 'bg-n-amber-9 text-white',
  resolved: 'bg-n-teal-9 text-white',
  closed: 'bg-n-slate-8 text-white',
  cancelled: 'bg-n-slate-8 text-white',
};
const statusPill = computed(
  () => STATUS_PILL[ticket.value?.status] || STATUS_PILL.open
);
const PRIORITY_FLAG = {
  urgent: 'text-n-ruby-9',
  high: 'text-n-amber-9',
  medium: 'text-n-blue-9',
  low: 'text-n-slate-9',
};

// CEA address is only relevant when it has content or contracts are enabled.
const hasAddress = computed(() => {
  const tk = ticket.value || {};
  return Boolean(
    tk.address_street ||
      tk.address_exterior_number ||
      tk.address_interior_number ||
      tk.address_colony ||
      tk.address_postal_code ||
      tk.address_city ||
      tk.address_state
  );
});
const addressSummary = computed(() => {
  const tk = ticket.value || {};
  // Exterior/interior numbers are first-class address columns; fold them into the street
  // segment so the Domicilio line always reads "Street Ext Int. Int, Colony, …".
  const street = [tk.address_street, tk.address_exterior_number]
    .filter(Boolean)
    .join(' ');
  const streetWithInterior = tk.address_interior_number
    ? `${street} Int. ${tk.address_interior_number}`
    : street;
  return [
    streetWithInterior,
    tk.address_colony,
    tk.address_postal_code,
    tk.address_city,
    tk.address_state,
  ]
    .filter(Boolean)
    .join(', ');
});

const channelLabel = computed(() => {
  const map = {
    whatsapp: t('TICKETS.CHANNELS.WHATSAPP'),
    in_person: t('TICKETS.CHANNELS.IN_PERSON'),
    email: t('TICKETS.CHANNELS.EMAIL'),
    phone: t('TICKETS.CHANNELS.PHONE'),
    facebook: t('TICKETS.CHANNELS.FACEBOOK'),
    instagram: t('TICKETS.CHANNELS.INSTAGRAM'),
    twitter: t('TICKETS.CHANNELS.TWITTER'),
  };
  return map[ticket.value?.channel] || ticket.value?.channel || '—';
});

const formatDay = d =>
  d
    ? new Date(d).toLocaleDateString(undefined, {
        day: 'numeric',
        month: 'short',
      })
    : '—';

// Inline description editing (rich text, same editor as comments)
const editingDescription = ref(false);
const descriptionDraft = ref('');
const startEditDescription = () => {
  descriptionDraft.value = ticket.value?.description || '';
  editingDescription.value = true;
};
const cancelDescription = () => {
  editingDescription.value = false;
};
const commitDescription = async () => {
  editingDescription.value = false;
  if (descriptionDraft.value === (ticket.value?.description || '')) return;
  await store.dispatch('tickets/updateTicket', {
    id: ticket.value.display_id,
    description: descriptionDraft.value,
  });
  await fetchTicket();
  emitter.emit('ticket_updated');
};

// Inline title editing
const titleDraft = ref('');
watch(
  () => ticket.value?.title,
  val => {
    titleDraft.value = val || '';
  },
  { immediate: true }
);
const commitTitle = async () => {
  const next = titleDraft.value.trim();
  if (!next || next === ticket.value?.title) {
    titleDraft.value = ticket.value?.title || '';
    return;
  }
  await store.dispatch('tickets/updateTicket', {
    id: ticket.value.display_id,
    title: next,
  });
  await fetchTicket();
  emitter.emit('ticket_updated');
};

const updateCountdown = () => {
  if (!ticket.value?.verification_expires_at) {
    verificationCountdown.value = '';
    return;
  }
  const expiresAt = new Date(ticket.value.verification_expires_at);
  const now = new Date();
  const diff = expiresAt - now;

  if (diff <= 0) {
    verificationCountdown.value = t('TICKETS.VERIFICATION.EXPIRED');
    return;
  }

  const hours = Math.floor(diff / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  verificationCountdown.value = t('TICKETS.VERIFICATION.EXPIRES_IN', {
    time: `${hours}h ${minutes}m`,
  });
};

const startCountdown = () => {
  if (countdownTimer) clearInterval(countdownTimer);
  if (isVerificationPending.value) {
    updateCountdown();
    countdownTimer = setInterval(updateCountdown, 60000);
  }
};

const onTaskModalKeydown = e => {
  if (e.key === 'Escape' && isTaskModalOpen.value) closeTaskModal();
};

// Lifecycle
onBeforeUnmount(() => {
  if (typingTimer) clearTimeout(typingTimer);
  if (countdownTimer) clearInterval(countdownTimer);
  if (ticket.value?.display_id) {
    sendTypingOff();
  }
  document.removeEventListener('click', closeDropdowns);
  document.removeEventListener('keydown', onTaskModalKeydown);
});

watch(assigneeOpen, async val => {
  if (val) {
    await nextTick();
    assigneeSearchInput.value?.focus();
  }
});

watch(teamOpen, async val => {
  if (val) {
    await nextTick();
    teamSearchInput.value?.focus();
  }
});

onMounted(async () => {
  document.addEventListener('click', closeDropdowns);
  document.addEventListener('keydown', onTaskModalKeydown);
  try {
    const ticketData = await fetchTicket();

    // Pass ticket data directly to ensure we have the correct ID
    await fetchConversations(ticketData);
    await fetchServiceOrders(ticketData);
    await fetchLinkedTasks(ticketData);

    await store.dispatch('agents/get');
    await store.dispatch('teams/get');
    fetchTicketCategories();
    startCountdown();
  } catch (error) {
    // Error in onMounted
  }
});

watch(resolvedTicketId, async (newId, oldId) => {
  if (newId && newId !== oldId) {
    isLoading.value = true;
    const ticketData = await fetchTicket();
    await fetchConversations(ticketData);
    await fetchServiceOrders(ticketData);
    await fetchLinkedTasks(ticketData);
    startCountdown();
  }
});

watch(
  () => ticket.value?.verification_status,
  () => {
    startCountdown();
  }
);
</script>

<template>
  <div v-if="ticket" class="flex flex-col h-full bg-n-background lg:flex-row">
    <!-- ============ LEFT: main content panel ============ -->
    <main class="flex flex-col flex-1 min-w-0 overflow-y-auto">
      <!-- Breadcrumb / context bar -->
      <div class="flex items-center justify-between gap-2 px-6 pt-4 pb-1">
        <div class="flex items-center gap-2 min-w-0 text-sm text-n-slate-11">
          <Button
            icon="i-lucide-arrow-left"
            variant="ghost"
            color="slate"
            size="sm"
            @click="goBack"
          />
          <span class="flex items-center gap-1.5">
            <span class="i-lucide-ticket text-n-slate-10" />
            {{ t('TICKETS.HEADER_TITLE') }}
          </span>
          <span class="text-n-slate-8">/</span>
          <span class="font-mono text-n-slate-10">
            {{ ticket.folio || `#${ticket.display_id}` }}
          </span>
          <span
            v-if="verificationStatusLabel"
            class="px-1.5 py-0.5 text-xs rounded-full"
            :class="verificationStatusClasses"
          >
            {{ verificationStatusLabel }}
          </span>
        </div>

        <div class="flex items-center gap-2 shrink-0">
          <Button
            v-if="ticket.status !== 'cancelled'"
            :label="
              ['resolved', 'closed'].includes(ticket.status)
                ? t('TICKETS.DETAIL_VIEW.RESOLVED_LABEL')
                : t('TICKETS.DETAIL_VIEW.RESOLVE_ACTION')
            "
            size="sm"
            :color="
              ['resolved', 'closed'].includes(ticket.status) ? 'teal' : 'blue'
            "
            @click="
              ['resolved', 'closed'].includes(ticket.status)
                ? null
                : openResolveModal()
            "
          />
          <!-- Overflow lifecycle actions -->
          <div ref="actionsRef" class="relative">
            <Button
              icon="i-lucide-ellipsis"
              variant="ghost"
              color="slate"
              size="sm"
              @click.stop="actionsOpen = !actionsOpen"
            />
            <ul
              v-if="actionsOpen"
              class="absolute right-0 z-50 py-1 mt-1 border rounded-lg shadow-lg top-full w-52 bg-n-solid-2 border-n-weak"
            >
              <li
                v-if="
                  ticket.status !== 'resolved' && ticket.status !== 'closed'
                "
                class="flex items-center gap-2 px-3 py-1.5 text-sm cursor-pointer text-n-slate-12 hover:bg-n-alpha-1"
                @click="
                  actionsOpen = false;
                  openResolveModal();
                "
              >
                <span class="i-lucide-circle-check text-n-teal-10" />
                {{ t('TICKETS.ACTIONS.RESOLVE') }}
              </li>
              <li
                v-if="canCloseTicket"
                class="flex items-center gap-2 px-3 py-1.5 text-sm cursor-pointer text-n-slate-12 hover:bg-n-alpha-1"
                @click="
                  actionsOpen = false;
                  closeTicket();
                "
              >
                <span class="i-lucide-lock text-n-slate-10" />
                {{ t('TICKETS.ACTIONS.CLOSE') }}
              </li>
              <li
                v-if="ticket.status === 'resolved'"
                class="flex items-center gap-2 px-3 py-1.5 text-sm cursor-pointer text-n-slate-12 hover:bg-n-alpha-1"
                @click="
                  actionsOpen = false;
                  reopenTicket();
                "
              >
                <span class="i-lucide-rotate-ccw text-n-slate-10" />
                {{ t('TICKETS.ACTIONS.REOPEN') }}
              </li>
              <li
                class="flex items-center gap-2 px-3 py-1.5 text-sm cursor-pointer text-n-slate-12 hover:bg-n-alpha-1"
                @click="
                  actionsOpen = false;
                  openFollowUpModal();
                "
              >
                <span class="i-lucide-copy-plus text-n-slate-10" />
                {{ t('TICKETS.FOLLOW_UP.MODAL_TITLE') }}
              </li>
              <li
                v-if="isServiceOrdersEnabled && canGenerateOrder"
                class="flex items-center gap-2 px-3 py-1.5 text-sm cursor-pointer text-n-slate-12 hover:bg-n-alpha-1"
                @click="
                  actionsOpen = false;
                  generateOrder();
                "
              >
                <span class="i-lucide-clipboard-list text-n-slate-10" />
                {{ t('TICKETS.GENERATE_ORDER.MENU_LABEL') }}
              </li>
              <li
                v-if="canSendLectura"
                class="flex items-center gap-2 px-3 py-1.5 text-sm cursor-pointer text-n-slate-12 hover:bg-n-alpha-1"
                @click="
                  actionsOpen = false;
                  sendLectura();
                "
              >
                <span class="i-lucide-gauge text-n-slate-10" />
                {{ t('TICKETS.DETAIL_VIEW.SEND_READING') }}
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div class="flex flex-col w-full max-w-3xl gap-6 px-6 pt-2 pb-10">
        <!-- Verification banner (only while a client confirmation is pending) -->
        <div
          v-if="isVerificationPending"
          class="flex flex-wrap items-center gap-3 px-4 py-2.5 border rounded-xl border-n-amber-6 bg-n-amber-3"
        >
          <span class="i-lucide-clock text-n-amber-11" />
          <span class="flex-1 min-w-0 text-sm text-n-amber-11">
            {{ t('TICKETS.VERIFICATION.STATUS.PENDING') }}
            <span v-if="verificationCountdown" class="text-n-amber-12">
              · {{ verificationCountdown }}
            </span>
          </span>
          <Button
            :label="t('TICKETS.VERIFICATION.VERIFY')"
            icon="i-lucide-check"
            size="xs"
            color="teal"
            @click="verifyTicket"
          />
          <Button
            :label="t('TICKETS.VERIFICATION.REJECT')"
            icon="i-lucide-x"
            size="xs"
            variant="ghost"
            color="ruby"
            @click="rejectVerification"
          />
        </div>

        <!-- Title + Tipo / Subtipo (same line, left-aligned) -->
        <div class="flex flex-wrap items-center gap-x-3 gap-y-2">
          <input
            v-model="titleDraft"
            class="flex-1 min-w-[12rem] text-2xl font-bold leading-snug bg-transparent rounded-md text-n-slate-12 placeholder:text-n-slate-9 focus:outline-none focus:bg-n-alpha-1 focus:px-2 focus:-mx-2 focus:py-1"
            @keyup.enter="$event.target.blur()"
            @blur="commitTitle"
          />

          <!-- Tipo (category) -->
          <div ref="categoryRef" class="relative shrink-0">
            <button
              type="button"
              class="inline-flex items-center gap-1.5 px-2 h-7 text-sm rounded-md hover:bg-n-alpha-1 text-n-slate-11 max-w-[12rem]"
              @click.stop="categoryOpen = !categoryOpen"
            >
              <span
                class="i-lucide-ticket shrink-0"
                :class="editCategoryId ? 'text-n-blue-9' : 'text-n-ruby-9'"
              />
              <span class="truncate">{{ categoryMenuLabel }}</span>
              <span class="i-lucide-chevron-down text-n-slate-10 shrink-0" />
            </button>
            <ul
              v-if="categoryOpen"
              class="absolute left-0 z-50 w-56 py-1 mt-1 overflow-y-auto border rounded-lg shadow-lg top-full bg-n-solid-2 border-n-weak max-h-60"
            >
              <li
                v-for="opt in categoryMenuOptions"
                :key="opt.value"
                class="flex items-center px-2.5 py-1.5 text-sm cursor-pointer hover:bg-n-alpha-1"
                :class="
                  opt.value === editCategoryId
                    ? 'text-n-blue-text font-medium'
                    : 'text-n-slate-12'
                "
                @click="onSelectCategory(opt.value)"
              >
                <span class="truncate">{{ opt.label }}</span>
              </li>
            </ul>
          </div>

          <!-- Subtipo (subcategory) -->
          <div ref="subcategoryRef" class="relative shrink-0">
            <button
              type="button"
              class="inline-flex items-center gap-1.5 px-2 h-7 text-sm rounded-md hover:bg-n-alpha-1 text-n-slate-11 max-w-[12rem]"
              @click.stop="subcategoryOpen = !subcategoryOpen"
            >
              <span
                class="i-lucide-tags shrink-0"
                :class="editSubcategoryId ? 'text-n-blue-9' : 'text-n-ruby-9'"
              />
              <span class="truncate">{{ subcategoryMenuLabel }}</span>
              <span class="i-lucide-chevron-down text-n-slate-10 shrink-0" />
            </button>
            <ul
              v-if="subcategoryOpen"
              class="absolute left-0 z-50 w-56 py-1 mt-1 overflow-y-auto border rounded-lg shadow-lg top-full bg-n-solid-2 border-n-weak max-h-60"
            >
              <li
                v-for="opt in subcategoryMenuOptions"
                :key="opt.value"
                class="flex items-center px-2.5 py-1.5 text-sm cursor-pointer hover:bg-n-alpha-1"
                :class="
                  opt.value === editSubcategoryId
                    ? 'text-n-blue-text font-medium'
                    : 'text-n-slate-12'
                "
                @click="onSelectSubcategory(opt.value)"
              >
                <span class="truncate">{{ opt.label }}</span>
              </li>
              <li
                v-if="!subcategoryMenuOptions.length"
                class="px-2.5 py-1.5 text-xs text-n-slate-10"
              >
                {{ t('TICKET_CATEGORIES.SELECTOR.SELECT_CATEGORY') }}
              </li>
            </ul>
          </div>
        </div>

        <!-- Metadata grid (two columns) -->
        <section class="grid grid-cols-1 gap-x-10 gap-y-3.5 sm:grid-cols-2">
          <!-- Status -->
          <div class="flex items-start gap-3 min-w-0">
            <span
              class="flex items-center gap-2 mt-1.5 text-sm w-28 shrink-0 text-n-slate-11"
            >
              <span class="i-lucide-loader text-n-slate-10" />
              {{ t('TICKETS.SORT.STATUS') }}
            </span>
            <div ref="statusRef" class="relative flex items-center">
              <button
                type="button"
                class="inline-flex items-center justify-between gap-1.5 px-2 h-7 w-36 text-sm font-medium rounded-md transition-all hover:brightness-110"
                :class="statusPill"
                @click.stop="statusOpen = !statusOpen"
              >
                <span class="truncate">{{
                  getStatusLabel(ticket.status)
                }}</span>
                <span class="i-lucide-chevron-down opacity-70 shrink-0" />
              </button>
              <ul
                v-if="statusOpen"
                class="absolute left-0 z-50 flex flex-col gap-0.5 p-1 mt-1 border rounded-lg shadow-lg top-full bg-n-solid-2 border-n-weak w-44"
              >
                <li
                  v-for="opt in statusOptions"
                  :key="opt.value"
                  class="flex items-center gap-2 px-2.5 py-1.5 text-sm rounded-md cursor-pointer hover:bg-n-alpha-1"
                  :class="
                    opt.value === ticket.status
                      ? 'text-n-slate-12 font-medium'
                      : 'text-n-slate-11'
                  "
                  @click="selectStatus(opt.value)"
                >
                  {{ opt.label }}
                  <span
                    v-if="opt.value === ticket.status"
                    class="ml-auto i-lucide-check text-n-blue-text"
                  />
                </li>
              </ul>
            </div>
          </div>

          <!-- Assignee -->
          <div class="flex items-start gap-3 min-w-0">
            <span
              class="flex items-center gap-2 mt-1.5 text-sm w-28 shrink-0 text-n-slate-11"
            >
              <span class="i-lucide-user text-n-slate-10" />
              {{ t('TICKETS.DETAIL_VIEW.FIELDS.ASSIGNEE') }}
            </span>
            <div ref="assigneeRef" class="relative flex-1 min-w-0">
              <button
                v-if="!assigneeOpen"
                type="button"
                class="flex items-center justify-between w-full gap-2 px-2 text-sm text-left rounded-md h-7 hover:bg-n-alpha-1"
                :class="
                  ticket.assignee_id ? 'text-n-slate-12' : 'text-n-slate-10'
                "
                @click.stop="assigneeOpen = true"
              >
                <span class="flex items-center gap-2 truncate">
                  <Avatar
                    v-if="ticket.assignee_id"
                    :name="currentAssigneeName"
                    :size="20"
                  />
                  <span class="truncate">{{ currentAssigneeName }}</span>
                </span>
                <span class="i-lucide-chevron-down text-n-slate-10 shrink-0" />
              </button>
              <div
                v-else
                class="flex items-center px-2 rounded-md h-7 outline-1 outline outline-n-brand bg-n-alpha-black2"
              >
                <input
                  ref="assigneeSearchInput"
                  v-model="assigneeSearch"
                  type="text"
                  class="w-full text-sm bg-transparent border-none reset-base text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none"
                  @click.stop
                />
              </div>
              <ul
                v-if="assigneeOpen"
                class="absolute left-0 right-0 z-50 py-1 mt-1 overflow-y-auto border rounded-lg shadow-lg bg-n-solid-2 border-n-weak max-h-44"
              >
                <li
                  v-for="agent in filteredAgents"
                  :key="agent.id ?? 'none'"
                  class="flex items-center gap-2 px-2.5 py-1.5 text-sm cursor-pointer hover:bg-n-alpha-1"
                  :class="
                    (agent.id ?? null) === (ticket.assignee_id ?? null)
                      ? 'text-n-blue-text font-medium'
                      : 'text-n-slate-12'
                  "
                  @mousedown.prevent="selectAssignee(agent.id)"
                >
                  <Avatar v-if="agent.id" :name="agent.name" :size="20" />
                  <span class="truncate">{{ agent.name }}</span>
                </li>
              </ul>
            </div>
          </div>

          <!-- Priority -->
          <div class="flex items-start gap-3 min-w-0">
            <span
              class="flex items-center gap-2 mt-1.5 text-sm w-28 shrink-0 text-n-slate-11"
            >
              <span
                class="i-lucide-flag"
                :class="PRIORITY_FLAG[ticket.priority]"
              />
              {{ t('TICKETS.SORT.PRIORITY') }}
            </span>
            <div ref="priorityRef" class="relative flex items-center">
              <button
                type="button"
                class="inline-flex items-center justify-between gap-2 px-2 text-sm rounded-md h-7 w-36 hover:bg-n-alpha-1 text-n-slate-12"
                @click.stop="priorityOpen = !priorityOpen"
              >
                <span class="flex items-center gap-2">
                  <span
                    class="i-lucide-flag"
                    :class="PRIORITY_FLAG[ticket.priority]"
                  />
                  {{ currentPriorityLabel }}
                </span>
                <span class="i-lucide-chevron-down text-n-slate-10" />
              </button>
              <ul
                v-if="priorityOpen"
                class="absolute left-0 z-50 flex flex-col gap-0.5 p-1 mt-1 border rounded-lg shadow-lg top-full bg-n-solid-2 border-n-weak w-44"
              >
                <li
                  v-for="opt in priorityOptions"
                  :key="opt.value"
                  class="flex items-center gap-2 px-2.5 py-1.5 text-sm rounded-md cursor-pointer hover:bg-n-alpha-1"
                  :class="
                    opt.value === ticket.priority
                      ? 'text-n-slate-12 font-medium'
                      : 'text-n-slate-11'
                  "
                  @click="selectPriority(opt.value)"
                >
                  <span
                    class="i-lucide-flag"
                    :class="PRIORITY_FLAG[opt.value]"
                  />
                  {{ opt.label }}
                  <span
                    v-if="opt.value === ticket.priority"
                    class="ml-auto i-lucide-check text-n-blue-text"
                  />
                </li>
              </ul>
            </div>
          </div>

          <!-- Team -->
          <div class="flex items-start gap-3 min-w-0">
            <span
              class="flex items-center gap-2 mt-1.5 text-sm w-28 shrink-0 text-n-slate-11"
            >
              <span class="i-lucide-users text-n-slate-10" />
              {{ t('TICKETS.DETAIL_VIEW.FIELDS.TEAM') }}
            </span>
            <div ref="teamRef" class="relative flex-1 min-w-0">
              <button
                v-if="!teamOpen"
                type="button"
                class="flex items-center justify-between w-full gap-2 px-2 text-sm text-left rounded-md h-7 hover:bg-n-alpha-1"
                :class="ticket.team_id ? 'text-n-slate-12' : 'text-n-slate-10'"
                @click.stop="teamOpen = true"
              >
                <span class="truncate">{{ currentTeamName }}</span>
                <span class="i-lucide-chevron-down text-n-slate-10 shrink-0" />
              </button>
              <div
                v-else
                class="flex items-center px-2 rounded-md h-7 outline-1 outline outline-n-brand bg-n-alpha-black2"
              >
                <input
                  ref="teamSearchInput"
                  v-model="teamSearch"
                  type="text"
                  class="w-full text-sm bg-transparent border-none reset-base text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none"
                  @click.stop
                />
              </div>
              <ul
                v-if="teamOpen"
                class="absolute left-0 right-0 z-50 py-1 mt-1 overflow-y-auto border rounded-lg shadow-lg bg-n-solid-2 border-n-weak max-h-44"
              >
                <li
                  v-for="team in filteredTeams"
                  :key="team.id ?? 'none'"
                  class="flex items-center gap-2 px-2.5 py-1.5 text-sm cursor-pointer hover:bg-n-alpha-1"
                  :class="
                    (team.id ?? null) === (ticket.team_id ?? null)
                      ? 'text-n-blue-text font-medium'
                      : 'text-n-slate-12'
                  "
                  @mousedown.prevent="selectTeam(team.id)"
                >
                  <span class="truncate">{{ team.name }}</span>
                </li>
              </ul>
            </div>
          </div>

          <!-- SLA -->
          <div class="flex items-start gap-3 min-w-0">
            <span
              class="flex items-center gap-2 mt-1.5 text-sm w-28 shrink-0 text-n-slate-11"
            >
              <span class="i-lucide-calendar-clock text-n-slate-10" />
              {{ t('TICKETS.DETAIL_VIEW.FIELDS.SLA') }}
            </span>
            <span
              class="mt-1 text-sm"
              :class="
                isOverdue ? 'text-n-ruby-11 font-medium' : 'text-n-slate-12'
              "
            >
              {{
                ticket.sla_deadline
                  ? formatDate(ticket.sla_deadline)
                  : t('TICKETS.NO_SLA')
              }}
            </span>
          </div>

          <!-- Channel -->
          <div class="flex items-start gap-3 min-w-0">
            <span
              class="flex items-center gap-2 mt-1.5 text-sm w-28 shrink-0 text-n-slate-11"
            >
              <span class="i-lucide-radio text-n-slate-10" />
              {{ t('TICKETS.SIDEBAR.CHANNEL') }}
            </span>
            <span class="mt-1 text-sm text-n-slate-12">{{ channelLabel }}</span>
          </div>
        </section>

        <!-- Description -->
        <section class="flex flex-col gap-2">
          <h3
            class="flex items-center gap-1.5 text-xs font-semibold tracking-wide uppercase text-n-slate-10"
          >
            <span class="i-lucide-align-left" />
            {{ t('TICKETS.DETAIL_VIEW.SECTIONS.DESCRIPTION') }}
          </h3>

          <div v-if="editingDescription" class="flex flex-col gap-2">
            <div class="p-2 border rounded-xl border-n-weak bg-n-solid-2">
              <WootMessageEditor
                v-model="descriptionDraft"
                :editor-id="`ticket-description-${resolvedTicketId}`"
                is-private
                :enable-canned-responses="false"
                :enable-variables="false"
                class="min-h-[80px] [&_.ProseMirror]:max-h-[300px] [&_.ProseMirror]:overflow-y-auto"
              />
            </div>
            <div class="flex justify-end gap-2">
              <Button
                :label="t('TICKETS.FORM.CANCEL')"
                variant="ghost"
                color="slate"
                size="sm"
                @click="cancelDescription"
              />
              <Button
                :label="t('TICKETS.DETAIL_VIEW.SAVE')"
                size="sm"
                color="blue"
                @click="commitDescription"
              />
            </div>
          </div>

          <div
            v-else
            class="p-4 text-sm leading-relaxed transition-colors border cursor-text rounded-xl border-n-weak bg-n-solid-2 min-h-[60px] hover:border-n-slate-6"
            @click="
              ticket.status !== 'resolved' && ticket.status !== 'closed'
                ? startEditDescription()
                : null
            "
          >
            <div
              v-if="ticket.description"
              v-dompurify-html="formatMessage(ticket.description, false, false)"
              class="prose prose-sm max-w-none text-n-slate-12 [&_.prosemirror-mention-node]:font-semibold"
            />
            <span v-else class="text-n-slate-10">
              {{ t('TICKETS.DETAILS.NO_DESCRIPTION') }}
            </span>
          </div>
        </section>

        <!-- Custom / personal attributes -->
        <TicketCustomAttributes
          :ticket="ticket"
          :workflow-fields="ticketWorkflowFields"
          :editable="ticket.status !== 'resolved' && ticket.status !== 'closed'"
          @updated="fetchTicket"
        />

        <!-- CEA Address (only when present or contracts enabled) -->
        <section
          v-if="hasAddress || isContractsEnabled"
          class="flex flex-col gap-2"
        >
          <h3
            class="flex items-center justify-between text-xs font-semibold tracking-wide uppercase text-n-slate-10"
          >
            <span class="flex items-center gap-1.5">
              <span class="i-lucide-map-pin" />
              {{ t('TICKETS.EDIT_ADDRESS.TITLE') }}
            </span>
            <button
              v-if="
                !isEditingAddress &&
                ticket.status !== 'resolved' &&
                ticket.status !== 'closed'
              "
              type="button"
              class="flex items-center justify-center w-6 h-6 rounded-md text-n-slate-10 hover:bg-n-alpha-1 hover:text-n-slate-12"
              @click="startEditingAddress"
            >
              <span class="i-lucide-pencil text-xs" />
            </button>
          </h3>

          <div
            v-if="isEditingAddress"
            class="flex flex-col gap-2 p-3 border rounded-xl border-n-weak bg-n-solid-2"
          >
            <input
              v-model="editAddress.address_street"
              type="text"
              :placeholder="t('TICKETS.EDIT_ADDRESS.STREET')"
              class="px-3 py-2 text-sm border rounded-lg reset-base border-n-weak bg-n-alpha-black2 text-n-slate-12"
            />
            <div class="grid grid-cols-2 gap-2">
              <input
                v-model="editAddress.address_exterior_number"
                type="text"
                :placeholder="t('TICKETS.EDIT_ADDRESS.EXTERIOR_NUMBER')"
                class="px-3 py-2 text-sm border rounded-lg reset-base border-n-weak bg-n-alpha-black2 text-n-slate-12"
              />
              <input
                v-model="editAddress.address_interior_number"
                type="text"
                :placeholder="t('TICKETS.EDIT_ADDRESS.INTERIOR_NUMBER')"
                class="px-3 py-2 text-sm border rounded-lg reset-base border-n-weak bg-n-alpha-black2 text-n-slate-12"
              />
              <input
                v-model="editAddress.address_colony"
                type="text"
                :placeholder="t('TICKETS.EDIT_ADDRESS.COLONY')"
                class="px-3 py-2 text-sm border rounded-lg reset-base border-n-weak bg-n-alpha-black2 text-n-slate-12"
              />
              <input
                v-model="editAddress.address_postal_code"
                type="text"
                :placeholder="t('TICKETS.EDIT_ADDRESS.POSTAL_CODE')"
                class="px-3 py-2 text-sm border rounded-lg reset-base border-n-weak bg-n-alpha-black2 text-n-slate-12"
              />
              <input
                v-model="editAddress.address_city"
                type="text"
                :placeholder="t('TICKETS.EDIT_ADDRESS.CITY')"
                class="px-3 py-2 text-sm border rounded-lg reset-base border-n-weak bg-n-alpha-black2 text-n-slate-12"
              />
              <input
                v-model="editAddress.address_state"
                type="text"
                :placeholder="t('TICKETS.EDIT_ADDRESS.STATE')"
                class="px-3 py-2 text-sm border rounded-lg reset-base border-n-weak bg-n-alpha-black2 text-n-slate-12"
              />
            </div>
            <div class="flex justify-end gap-2">
              <Button
                variant="ghost"
                color="slate"
                size="xs"
                :disabled="isSavingAddress"
                :label="t('TICKETS.FORM.CANCEL')"
                @click="cancelEditingAddress"
              />
              <Button
                size="xs"
                color="blue"
                :is-loading="isSavingAddress"
                :label="t('TICKETS.EDIT_ADDRESS.SAVE')"
                @click="saveAddress"
              />
            </div>
          </div>
          <p
            v-else-if="hasAddress"
            class="p-3 text-sm border rounded-xl border-n-weak bg-n-solid-2 text-n-slate-12"
          >
            {{ addressSummary }}
          </p>
          <p v-else class="px-1 text-sm text-n-slate-10">
            {{ t('TICKETS.EDIT_ADDRESS.EMPTY') }}
          </p>
        </section>

        <!-- Contract (CEA — only when contracts enabled) -->
        <section v-if="isContractsEnabled" class="flex flex-col gap-2">
          <h3
            class="flex items-center justify-between text-xs font-semibold tracking-wide uppercase text-n-slate-10"
          >
            <span class="flex items-center gap-1.5">
              <span class="i-lucide-file-text" />
              {{ t('TICKETS.EDIT_CONTRACT.TITLE') }}
            </span>
            <button
              v-if="
                !isEditingContract &&
                ticket.status !== 'resolved' &&
                ticket.status !== 'closed'
              "
              type="button"
              class="flex items-center justify-center w-6 h-6 rounded-md text-n-slate-10 hover:bg-n-alpha-1 hover:text-n-slate-12"
              @click="startEditingContract"
            >
              <span class="i-lucide-pencil text-xs" />
            </button>
          </h3>
          <div v-if="isEditingContract" class="flex items-center gap-2">
            <input
              v-model="editContract"
              type="text"
              :placeholder="t('TICKETS.EDIT_CONTRACT.PLACEHOLDER')"
              class="flex-1 px-3 py-2 text-sm border rounded-lg reset-base border-n-weak bg-n-alpha-black2 text-n-slate-12"
            />
            <Button
              variant="ghost"
              color="slate"
              size="xs"
              :disabled="isSavingContract"
              :label="t('TICKETS.FORM.CANCEL')"
              @click="cancelEditingContract"
            />
            <Button
              size="xs"
              color="blue"
              :is-loading="isSavingContract"
              :label="t('TICKETS.EDIT_CONTRACT.SAVE')"
              @click="saveContract"
            />
          </div>
          <p
            v-else-if="ticket.contract_number"
            class="px-1 font-mono text-sm text-n-slate-12"
          >
            {{ ticket.contract_number }}
          </p>
          <p v-else class="px-1 text-sm text-n-slate-10">
            {{ t('TICKETS.EDIT_CONTRACT.EMPTY') }}
          </p>
        </section>

        <!-- Service Orders (only when enabled and present) -->
        <section
          v-if="isServiceOrdersEnabled && serviceOrders.length"
          class="flex flex-col gap-2"
        >
          <h3
            class="flex items-center gap-1.5 text-xs font-semibold tracking-wide uppercase text-n-slate-10"
          >
            <span class="i-lucide-clipboard-list" />
            {{ t('TICKETS.ACTIONS_LABELS.SERVICE_ORDERS') }}
            <span class="text-n-slate-9">{{ serviceOrders.length }}</span>
          </h3>
          <div class="flex flex-col gap-2">
            <button
              v-for="order in serviceOrders"
              :key="order.id"
              type="button"
              class="flex flex-col gap-1 p-3 text-left transition-colors border rounded-xl border-n-weak bg-n-solid-2 hover:border-n-slate-6"
              @click="goToOrder(order.id)"
            >
              <div class="flex items-center justify-between">
                <span class="font-mono text-xs text-n-slate-11">
                  {{ t('TICKETS.ACTIONS_LABELS.HASH') }}{{ order.id }}
                </span>
                <span
                  class="px-2 py-0.5 text-xs rounded-full bg-n-alpha-2 text-n-slate-11"
                >
                  {{ order.status }}
                </span>
              </div>
              <span class="text-sm font-medium text-n-slate-12">
                {{ order.order_type }}
              </span>
            </button>
          </div>
        </section>

        <!-- Linked Tasks (only when feature enabled) -->
        <section v-if="isTasksEnabled" class="flex flex-col gap-2">
          <h3
            class="flex items-center gap-1.5 text-xs font-semibold tracking-wide uppercase text-n-slate-10"
          >
            <span class="i-lucide-list-tree" />
            {{ t('TASKS.HEADER_TITLE') }}
            <span v-if="linkedTasks.length" class="text-n-slate-9">
              {{ linkedTasks.length }}
            </span>
          </h3>
          <div
            class="flex flex-col border divide-y rounded-xl divide-n-weak border-n-weak bg-n-solid-2"
          >
            <button
              v-for="task in linkedTasks"
              :key="task.id"
              type="button"
              class="flex items-center gap-2.5 px-3 py-2 text-left hover:bg-n-alpha-1"
              @click="goToTask(task.display_id)"
            >
              <span class="font-mono text-xs text-n-slate-9">
                {{ t('TASKS.NUMBER_PREFIX') }}{{ task.display_id }}
              </span>
              <span class="flex-1 min-w-0 text-sm truncate text-n-slate-12">
                {{ task.title }}
              </span>
              <span
                v-if="task.blocked"
                class="px-1.5 py-0.5 text-xs rounded bg-n-amber-9/10 text-n-amber-11"
              >
                {{ t('TASKS.BADGES.BLOCKED') }}
              </span>
            </button>
            <div
              class="flex items-center gap-2.5 px-3 h-10 focus-within:bg-n-alpha-1"
            >
              <span class="i-lucide-plus text-n-slate-9 shrink-0" />
              <input
                v-model="newTaskTitle"
                type="text"
                :placeholder="t('TASKS.CREATE_PROMPT_TITLE')"
                class="flex-1 min-w-0 text-sm bg-transparent border-none reset-base text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none"
                @keyup.enter="createTaskForTicket"
              />
              <Button
                size="xs"
                color="blue"
                :label="t('TASKS.ACTIONS.NEW')"
                :is-loading="isCreatingTask"
                :disabled="!newTaskTitle.trim() || isCreatingTask"
                @click="createTaskForTicket"
              />
            </div>
          </div>
        </section>

        <!-- Attachments -->
        <section class="flex flex-col gap-2">
          <TicketAttachments
            :ticket="ticket"
            :readonly="
              ticket.status === 'resolved' || ticket.status === 'closed'
            "
            @update="fetchTicket"
          />
        </section>

        <!-- Document requests (ask the client) + collected documents -->
        <section v-if="ticket.display_id" class="flex flex-col gap-3">
          <h3
            class="flex items-center gap-1.5 text-xs font-semibold tracking-wide uppercase text-n-slate-10"
          >
            <span class="i-lucide-file-check-2" />
            {{ t('TICKETS.DOCUMENTS.HEADER_TITLE') }}
          </h3>
          <DocumentRequestsPanel
            :api="ticketDocumentRequestsApi"
            :owner-id="ticket.display_id"
            :share-fn="documentShareFn"
          />
          <DocumentFileList
            :api="ticketDocumentsApi"
            :owner-id="ticket.display_id"
          />
        </section>

        <!-- Digital signatures (Cincel) -->
        <section v-if="ticket.display_id" class="flex flex-col gap-3">
          <h3
            class="flex items-center gap-1.5 text-xs font-semibold tracking-wide uppercase text-n-slate-10"
          >
            <span class="i-lucide-signature" />
            {{ t('TICKETS.SIGNATURES.HEADER_TITLE') }}
          </h3>
          <SignatureRequestsPanel :owner-id="ticket.display_id" />
        </section>
      </div>
    </main>

    <!-- ============ RIGHT: activity / comments / conversation panel ============ -->
    <aside
      class="flex flex-col w-full border-t lg:w-[30rem] lg:h-full lg:border-t-0 lg:border-l border-n-weak bg-n-solid-1"
    >
      <!-- Tabs -->
      <header
        class="flex items-center flex-shrink-0 gap-1 px-3 border-b border-n-weak"
      >
        <button
          v-for="tab in ['activity', 'conversation']"
          v-show="tab !== 'conversation' || ticket.conversation"
          :key="tab"
          type="button"
          class="px-3 py-3 -mb-px text-sm font-medium border-b-2 transition-colors"
          :class="
            activeTab === tab
              ? 'border-n-brand text-n-slate-12'
              : 'border-transparent text-n-slate-10 hover:text-n-slate-12'
          "
          @click="activeTab = tab"
        >
          {{ t(`TICKETS.DETAIL_VIEW.TABS.${tab.toUpperCase()}`) }}
        </button>
      </header>

      <!-- Actividad tab: combined comments + activity feed with composer -->
      <template v-if="activeTab === 'activity'">
        <div class="flex flex-col flex-1 gap-3 px-4 py-4 overflow-y-auto">
          <!-- Ticket creation (start of the timeline) -->
          <div
            v-if="ticket.created_at"
            class="flex items-start gap-2 text-xs text-n-slate-11"
          >
            <span
              class="mt-1.5 w-1.5 h-1.5 rounded-full bg-n-slate-7 shrink-0"
            />
            <div class="flex flex-wrap items-center flex-1 min-w-0 gap-x-1.5">
              <span>{{ t('TICKETS.DETAIL_VIEW.CREATED') }}</span>
              <span class="text-n-slate-9">{{
                formatDate(ticket.created_at)
              }}</span>
              <template v-if="ticket.created_by">
                <span class="text-n-slate-9">
                  {{ t('TICKETS.DETAIL_VIEW.BY') }}
                </span>
                <span class="font-medium text-n-slate-11">
                  {{ ticket.created_by.name }}
                </span>
              </template>
            </div>
          </div>

          <template v-for="conv in ticketConversations" :key="conv.id">
            <!-- Activity entry (system-generated) -->
            <div
              v-if="conv.message_type === 'activity'"
              class="flex items-start gap-2 text-xs text-n-slate-11"
            >
              <span
                class="mt-1.5 w-1.5 h-1.5 rounded-full bg-n-slate-7 shrink-0"
              />
              <div class="flex-1 min-w-0">
                <span>{{ conv.message }}</span>
                <span class="ml-1.5 text-n-slate-9">
                  {{ formatDay(conv.created_at) }}
                </span>
              </div>
            </div>

            <!-- Comment -->
            <div v-else class="flex gap-2.5">
              <Avatar :name="conv.sender_name || 'System'" :size="28" />
              <div class="flex-1 min-w-0">
                <div class="flex items-center gap-2 mb-0.5">
                  <span class="text-sm font-medium text-n-slate-12">
                    {{ conv.sender_name }}
                  </span>
                  <span class="text-xs text-n-slate-9">
                    {{ formatDate(conv.created_at) }}
                  </span>
                  <span
                    v-if="conv.private"
                    class="px-1.5 py-0.5 text-xs rounded-full bg-n-amber-9/20 text-n-amber-11"
                  >
                    {{ t('TICKETS.ACTIONS_LABELS.PRIVATE') }}
                  </span>
                </div>
                <p
                  v-dompurify-html="
                    formatMessage(conv.message, false, conv.private)
                  "
                  class="text-sm text-n-slate-12 prose prose-sm max-w-none [&_.prosemirror-mention-node]:font-semibold"
                />
              </div>
            </div>
          </template>
        </div>

        <!-- Composer -->
        <div class="flex-shrink-0 p-3 border-t border-n-weak">
          <div
            v-if="typingUserNames"
            class="px-1 mb-1 text-xs italic text-n-slate-11"
          >
            {{ typingUserNames }}
          </div>
          <div
            class="p-2 border rounded-lg border-n-weak bg-n-solid-2"
            :class="{ 'bg-n-amber-9/10 border-n-amber-9/30': isPrivate }"
          >
            <WootMessageEditor
              v-model="newComment"
              :editor-id="`ticket-comment-${resolvedTicketId}`"
              :placeholder="t('TICKETS.DETAILS.ADD_COMMENT')"
              is-private
              :enable-canned-responses="false"
              :enable-variables="false"
              :focus-on-mount="false"
              class="min-h-[52px] [&_.ProseMirror]:max-h-[180px] [&_.ProseMirror]:overflow-y-auto"
              @typing-on="onCommentInput"
              @typing-off="sendTypingOff"
            />
          </div>
          <div class="flex items-center justify-between mt-2">
            <div class="flex items-center gap-4">
              <label
                class="flex items-center gap-2 text-sm cursor-pointer text-n-slate-11"
              >
                <input v-model="isPrivate" type="checkbox" class="rounded" />
                {{ t('TICKETS.DETAILS.PRIVATE_NOTE') }}
              </label>
              <label
                v-if="isPrivate"
                class="flex items-center gap-2 text-sm cursor-pointer text-n-amber-11"
              >
                <input
                  v-model="clientVisible"
                  type="checkbox"
                  class="rounded"
                />
                {{ t('TICKETS.DETAILS.CLIENT_VISIBLE') }}
              </label>
            </div>
            <Button
              icon="i-lucide-send-horizontal"
              size="sm"
              color="blue"
              :label="t('TICKETS.DETAILS.SEND')"
              :disabled="!newComment.trim()"
              @click="addComment"
            />
          </div>
        </div>
      </template>

      <!-- Conversación tab: the real inbox conversation UI (read + reply) -->
      <TicketConversationThread
        v-else-if="ticket.conversation"
        :conversation-id="ticket.conversation.display_id"
      />
    </aside>

    <!-- Resolve Modal -->
    <ResolveTicketModal
      v-if="showResolveModal"
      :show="showResolveModal"
      :ticket="ticket"
      @close="showResolveModal = false"
      @resolved="onResolved"
    />

    <!-- Generate Order Modal -->
    <GenerateOrderModal
      v-if="isServiceOrdersEnabled && showGenerateOrderModal"
      :show="showGenerateOrderModal"
      :ticket="ticket"
      @close="showGenerateOrderModal = false"
      @generated="onOrderGenerated"
    />

    <!-- Follow-up Ticket Modal -->
    <teleport to="body">
      <div
        v-if="showFollowUpModal"
        class="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
        @click.self="showFollowUpModal = false"
      >
        <div
          class="w-full max-w-md border shadow-xl rounded-xl bg-n-solid-2 border-n-weak"
        >
          <div class="p-4 border-b border-n-weak">
            <h3 class="text-lg font-semibold text-n-slate-12">
              {{ t('TICKETS.FOLLOW_UP.MODAL_TITLE') }}
            </h3>
          </div>
          <div class="flex flex-col gap-4 p-4">
            <div>
              <label class="block mb-1 text-sm font-medium text-n-slate-11">
                {{ t('TICKETS.FOLLOW_UP.TITLE_LABEL') }}
              </label>
              <input
                v-model="followUpTitle"
                type="text"
                class="w-full px-3 py-2 border rounded-lg bg-n-solid-3 border-n-weak text-n-slate-12"
              />
            </div>
            <div>
              <label class="block mb-1 text-sm font-medium text-n-slate-11">
                {{ t('TICKETS.FOLLOW_UP.DESCRIPTION_LABEL') }}
              </label>
              <textarea
                v-model="followUpDescription"
                rows="3"
                :placeholder="t('TICKETS.FOLLOW_UP.DESCRIPTION_PLACEHOLDER')"
                class="w-full px-3 py-2 border rounded-lg resize-none bg-n-solid-3 border-n-weak text-n-slate-12"
              />
            </div>
          </div>
          <div class="flex justify-end gap-2 p-4 border-t border-n-weak">
            <Button
              variant="ghost"
              color="slate"
              :disabled="isCreatingFollowUp"
              :label="t('TICKETS.FORM.CANCEL')"
              @click="showFollowUpModal = false"
            />
            <Button
              color="blue"
              :disabled="!followUpTitle.trim()"
              :is-loading="isCreatingFollowUp"
              :label="t('TICKETS.FOLLOW_UP.CONFIRM')"
              @click="createFollowUp"
            />
          </div>
        </div>
      </div>
    </teleport>

    <!-- Linked Task detail modal -->
    <Teleport to="body">
      <div
        v-if="isTaskModalOpen"
        class="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-8 bg-n-alpha-black1 backdrop-blur-sm"
        @click.self="closeTaskModal"
      >
        <div
          class="relative flex flex-col w-full max-w-6xl h-[88vh] overflow-hidden shadow-2xl rounded-2xl bg-n-background outline outline-1 outline-n-container"
        >
          <button
            type="button"
            class="absolute z-20 flex items-center justify-center w-8 h-8 rounded-lg top-3 right-3 text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12"
            @click="closeTaskModal"
          >
            <span class="i-lucide-x" />
          </button>
          <TaskDetails
            :key="selectedTaskId"
            :task-id="selectedTaskId"
            embedded
            @navigate="onTaskModalNavigate"
            @close="closeTaskModal"
          />
        </div>
      </div>
    </Teleport>
  </div>

  <!-- Loading state -->
  <div
    v-else-if="isLoading"
    class="flex items-center justify-center w-full h-full"
  >
    <Spinner />
  </div>
</template>
